import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import type { Product } from '../types';
import { foto } from '../imagenes';

interface CircularProductCardProps {
  product: Product;
  variant?: 'default' | 'innovation';
}

/**
 * Tarjeta circular con el nombre del equipo describiendo un arco sobre la foto.
 *
 * Es un enlace real (no un div con onClick): así se puede abrir en otra
 * pestaña, se navega con teclado y los buscadores la siguen.
 */
const CircularProductCard: React.FC<CircularProductCardProps> = ({
  product,
  variant = 'default',
}) => {
  const isInnovation = variant === 'innovation';
  const pathId = `arc-${product.id}`;

  return (
    <Reveal variant="scale" className="group relative flex flex-col items-center justify-center py-5 md:py-12">
      <Link
        to={`/catalogo/${product.id}`}
        className="flex w-full flex-col items-center rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-cabina-red/50"
        aria-label={`${product.name}: ${product.shortDescription}`}
      >
        {/* Nombre en arco sobre la foto */}
        <div className="pointer-events-none absolute inset-x-0 -top-5 bottom-0 z-20 origin-center md:-top-12">
          <svg viewBox="0 0 500 500" className="h-full w-full overflow-visible" aria-hidden="true">
            <defs>
              <path id={pathId} d="M 50,250 A 200,200 0 0,1 450,250" fill="transparent" />
            </defs>
            <text className="origin-center fill-gray-900 font-sans text-[34px] font-black uppercase tracking-[0.22em] transition-[fill,transform] duration-700 [transform-box:fill-box] group-hover:-rotate-2 group-hover:scale-105 group-hover:fill-cabina-red sm:text-[24px] md:text-[30px]">
              <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
                {product.name}
              </textPath>
            </text>
          </svg>
        </div>

        {/* Foto circular */}
        <div className="relative aspect-square w-[76%] rounded-full p-1.5 sm:w-64 sm:p-2 md:h-72 md:w-72 lg:h-auto lg:w-full lg:max-w-80">
          {/* Anillo punteado que gira */}
          <div
            className={`absolute inset-0 animate-[spin_15s_linear_infinite] rounded-full border-2 border-dashed ${
              isInnovation ? 'border-cabina-red/20' : 'border-cabina-red/10'
            } group-hover:border-cabina-red/40 group-hover:animate-[spin_8s_linear_infinite]`}
          />

          {/* Resplandor al pasar el cursor */}
          <div
            className={`absolute inset-0 rounded-full ${
              isInnovation ? 'bg-cabina-red/10' : 'bg-cabina-red/5'
            } opacity-0 blur-3xl transition-opacity duration-1000 group-hover:opacity-100`}
          />

          <div className="relative z-10 h-full w-full overflow-hidden rounded-full border-4 border-white shadow-2xl transition-all duration-700 sm:border-[8px] ease-out group-hover:-translate-y-2 group-hover:shadow-cabina-red/20">
            <img
              {...foto(product.image, '(min-width: 1024px) 320px, (min-width: 768px) 288px, (min-width: 640px) 256px, 38vw')}
              alt={`${product.name} — ${product.shortDescription}`}
              loading="lazy"
              decoding="async"
              className={`h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.15] ${
                isInnovation ? 'group-hover:-rotate-3' : 'group-hover:rotate-3'
              }`}
            />

            <div className="absolute inset-0 bg-gradient-to-tr from-cabina-red/30 via-transparent to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />

            {/* Barrido de luz */}
            <div className="absolute inset-0 translate-x-[-200%] skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:animate-[shine_1.5s_ease-out_infinite]" />
          </div>
        </div>

        {/* Distintivo que sube al pasar el cursor */}
        <div className="pointer-events-none absolute bottom-4 left-1/2 z-30 -translate-x-1/2 scale-90 opacity-0 transition-all duration-500 group-hover:-translate-y-[15px] group-hover:scale-100 group-hover:opacity-100">
          <span
            className={`flex items-center gap-3 whitespace-nowrap rounded-full px-8 py-2.5 text-[10px] font-bold uppercase tracking-widest shadow-2xl backdrop-blur-xl ${
              isInnovation
                ? 'border border-white/10 bg-cabina-red text-white'
                : 'border border-white/10 bg-black/90 text-white'
            }`}
          >
            {isInnovation ? 'Ver más' : 'Explorar experiencia'}
            <span className={`text-lg ${isInnovation ? 'text-white' : 'text-cabina-red'}`}>→</span>
          </span>
        </div>
      </Link>
    </Reveal>
  );
};

export default CircularProductCard;
